import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useVisibleContacts } from './useCrm';
import { duplicateGroups, isStrong } from '../../services/dedupe';
import { mergeContacts } from '../../services/contactMerge';
import { ui, MUTED, DANGER, SELECTED } from './crmUi';

// Admin-only. Suggests contacts that look like the same person (same phone, email or full name).
// Nothing is merged unless the admin picks the one to keep and clicks Merge.
const nameOf = (c) => [c.firstName, c.lastName].filter(Boolean).join(' ') || c.name || '(no name)';
const fmt = (p) => String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
const LABEL = { phone: 'same phone', email: 'same email', name: 'same name' };

export default function DuplicatesPage() {
  const { user, userRole } = useAuth();
  const isAdmin = userRole === 'Admin';
  const navigate = useNavigate();
  const contacts = useVisibleContacts({ includeArchived: true });
  const byId = useMemo(() => Object.fromEntries(contacts.map((c) => [c.id, c])), [contacts]);
  const groups = useMemo(() => duplicateGroups(contacts), [contacts]);
  const [keep, setKeep] = useState({});
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [hidden, setHidden] = useState({});

  if (!isAdmin) return <div style={ui.page}><h1 style={ui.h1}>Duplicates</h1><p>Only admins can review and merge duplicates.</p></div>;

  const merge = async (g, key) => {
    const keepId = keep[key] || g.ids[0];
    const others = g.ids.filter((id) => id !== keepId && byId[id]);
    if (!others.length) return;
    if (!window.confirm(`Keep ${nameOf(byId[keepId])} and merge ${others.length} other record(s) into it?\n\nTheir notes, tasks, timeline, pipeline and transaction links move to the kept contact, and the duplicates are removed. This can't be undone from the app.`)) return;
    setBusy(key); setMsg('');
    try { for (const o of others) await mergeContacts(keepId, o, user?.uid); setMsg(`Merged into ${nameOf(byId[keepId])}.`); }
    catch (e) { setMsg(`Could not merge: ${e.message}`); } finally { setBusy(''); }
  };

  const shown = groups.filter((g) => !hidden[g.ids.join('|')]);
  return (
    <div style={ui.page}>
      <h1 style={ui.h1}>Duplicates</h1>
      <p style={ui.sub}>Contacts that look like the same person. Nothing changes until you pick the one to keep and click Merge. "Same name" only is a weaker match: check before merging.</p>
      {msg && <div style={{ ...ui.card, fontWeight: 700, color: msg.startsWith('Could not') ? DANGER : '#1d6b2f' }}>{msg}</div>}
      {!shown.length && <div style={ui.card}>No duplicates found.</div>}
      {shown.map((g) => {
        const key = g.ids.join('|'); const keepId = keep[key] || g.ids[0];
        return (
          <div key={key} style={{ ...ui.card, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{g.reasons.map((r) => LABEL[r]).join(', ')}{!isStrong(g.reasons) && <span style={{ color: DANGER }}> (name only: check carefully)</span>}</div>
            {g.ids.filter((id) => byId[id]).map((id) => { const c = byId[id]; return (
              <label key={id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 6, background: keepId === id ? SELECTED : 'transparent', borderRadius: 6 }}>
                <input type="radio" checked={keepId === id} onChange={() => setKeep({ ...keep, [key]: id })} />
                <span><strong>{nameOf(c)}</strong>{keepId === id ? ' (keep)' : ''}<br />
                  <span style={{ fontSize: 13, color: MUTED }}>{[fmt(c.phone), c.email, c.address, c.source, c.archived ? 'Archived' : '', c.dnc ? 'Do Not Call' : '', c.createdAt ? `Added ${String(c.createdAt).slice(0, 10)}` : ''].filter(Boolean).join(' · ')}</span>{' '}
                  <button type="button" style={{ border: 0, background: 'none', color: '#1a5fb4', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate(`/leads/contacts?id=${id}`)}>Open</button></span>
              </label>); })}
            <div style={{ ...ui.row, marginTop: 8 }}>
              <button style={ui.btn} disabled={!!busy} onClick={() => merge(g, key)}>{busy === key ? 'Merging...' : 'Merge into the selected contact'}</button>
              <button style={ui.ghost} onClick={() => setHidden({ ...hidden, [key]: true })}>Not duplicates (hide)</button>
            </div>
          </div>);
      })}
    </div>
  );
}
